import { useLanguage } from '../context/LanguageContext';
import Badge from './ui/Badge';
import GlassCard from './ui/GlassCard';
import Button from './ui/Button';

const DEMO_VIDEO_ID = 'OJL7ew_wF28';

const features = [
  {
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    title: { en: 'Multi-Branch Management', ar: 'إدارة الفروع المتعددة' },
    desc: { en: 'Manage all branches from one dashboard', ar: 'إدارة جميع الفروع من لوحة تحكم واحدة' },
  },
  {
    icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    title: { en: 'Online Payments', ar: 'المدفوعات الإلكترونية' },
    desc: { en: 'Mada, STC Pay, Apple Pay, Visa, Mastercard', ar: 'مدى، STC Pay، Apple Pay، فيزا، ماستركارد' },
  },
  {
    icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    title: { en: 'SMS & Announcements', ar: 'الرسائل والتنبيهات' },
    desc: { en: 'Automated reminders and parent communication', ar: 'تذكيرات تلقائية وتواصل مع أولياء الأمور' },
  },
  {
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    title: { en: 'Attendance & Evaluations', ar: 'الحضور والتقييمات' },
    desc: { en: 'Track sessions, attendance, and player performance', ar: 'تتبع الحصص والحضور وأداء اللاعبين' },
  },
  {
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    title: { en: 'Financial Reports', ar: 'التقارير المالية' },
    desc: { en: 'Revenue, expenses, and subscription analytics', ar: 'الإيرادات والمصروفات وتحليلات الاشتراكات' },
  },
  {
    icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
    title: { en: 'Arabic & RTL', ar: 'العربية واتجاه RTL' },
    desc: { en: 'Full bilingual support for Saudi market', ar: 'دعم كامل للغة العربية والسوق السعودي' },
  },
];

export default function AcademyDemo() {
  const { t, language } = useLanguage();
  const fa = t.forAcademies;
  const getText = (obj) => obj[language] || obj.en || obj.ar;

  return (
    <div id="academy-demo" className="relative z-10 scroll-mt-24">
      <section className="w-full min-h-[50vh] md:min-h-[60vh] flex items-center justify-center pb-8 md:pb-12 px-4 md:px-6 lg:px-12">
        <div className="max-w-7xl w-full text-center space-y-8">
          <Badge>{fa.badge}</Badge>
          <h2 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight">
            {fa.heroTitle1} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary drop-shadow-sm">
              {fa.heroTitle2}
            </span>{' '}
            <br />
            {fa.heroTitle3}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {fa.heroDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <Button to="#academy-demo-video" size="lg">
              {fa.watchDemo}
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </Button>
            <Button to="#contact" variant="outline" size="lg">
              {fa.requestDemo}
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </Button>
          </div>
        </div>
      </section>

      <section
        id="academy-demo-video"
        className="w-full py-12 md:py-24 px-4 md:px-6 lg:px-12 scroll-mt-24"
      >
        <div className="max-w-4xl mx-auto">
          <GlassCard hover3d className="p-4 md:p-8">
            <div className="space-y-6">
              <div className="text-center">
                <Badge>{fa.videoBadge}</Badge>
                <h3 className="text-2xl font-bold mt-4 text-secondary dark:text-white">
                  {fa.videoTitle}
                </h3>
              </div>
              <div className="aspect-video w-full rounded-xl overflow-hidden bg-black/20">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${DEMO_VIDEO_ID}?rel=0`}
                  title="Sports Academy Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      <section className="w-full py-12 md:py-24 px-4 md:px-6 lg:px-12 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <GlassCard hover3d className="p-8 lg:p-12">
            <div className="space-y-12">
              <div className="text-center space-y-4">
                <Badge>{fa.featuresBadge}</Badge>
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                    {fa.featuresTitle}
                  </span>
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, idx) => (
                  <GlassCard
                    key={idx}
                    className="p-6 backdrop-blur-md hover:shadow-lg shadow-primary/10 transition-all duration-500"
                  >
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                        <svg
                          className="w-7 h-7 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={feature.icon}
                          />
                        </svg>
                      </div>
                      <p className="text-gray-800 dark:text-gray-200 font-bold">
                        {getText(feature.title)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {getText(feature.desc)}
                      </p>
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
}
